import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import useStyles from "./style";
import MplusApi from "Services/Mplus";
import {
  BODY_LABELS,
  FUEL_LABELS,
  TRANSMISSION_LABELS,
  apiErrorMessage,
  fieldErrors,
  formatAmount,
  productFeatures,
  toAssetUrl,
  unwrapList,
} from "Pages/ProgrammeMPlus/mplusUtils";

const EMPTY = {
  name: "",
  product_code: "",
  category: "",
  unit_cost: "",
  annual_interest_rate: "",
  specs: "",
  fuel_type: "",
  transmission: "",
  body_type: "",
  is_active: true,
};

const SELECTS = [
  { key: "fuel_type", label: "Énergie", options: FUEL_LABELS },
  { key: "transmission", label: "Boîte", options: TRANSMISSION_LABELS },
  { key: "body_type", label: "Carrosserie", options: BODY_LABELS },
];

const formFrom = (product) => ({
  ...EMPTY,
  ...Object.keys(EMPTY).reduce((result, key) => ({ ...result, [key]: product?.[key] ?? EMPTY[key] }), {}),
});

// Corps de la requête : JSON (null efface un champ) ou FormData si une photo est jointe.
const buildBody = (form, image, isEdit) => {
  const values = {
    name: form.name.trim(),
    product_code: form.product_code.trim(),
    category: form.category.trim(),
    unit_cost: form.unit_cost === "" ? undefined : Number(form.unit_cost),
    annual_interest_rate: form.annual_interest_rate === "" ? undefined : Number(form.annual_interest_rate),
    specs: form.specs.trim() || undefined,
    fuel_type: form.fuel_type || (isEdit ? null : undefined),
    transmission: form.transmission || (isEdit ? null : undefined),
    body_type: form.body_type || (isEdit ? null : undefined),
    is_active: form.is_active,
  };
  if (!isEdit) Object.assign(values, { program_code: "METUAA_MPLUS", currency: "XAF" });
  if (!image) return values;
  const data = new FormData();
  // En multipart, une chaîne vide est ignorée par l'API : on n'envoie que les valeurs renseignées.
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) data.append(key, String(value));
  });
  data.append("product_image", image);
  return data;
};

const ProductCatalogDialog = ({ open, onClose, onChanged, notify }) => {
  const classes = useStyles();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null : liste ; {} : création ; produit : modification
  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Back-office : tous les modèles, actifs ou non.
      setProducts(unwrapList(await MplusApi.getProductCatalog({ is_active: undefined, limit: 200 })));
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de charger le catalogue."), "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (open) load();
  }, [load, open]);

  const startEdit = (product) => {
    setEditing(product || {});
    setForm(formFrom(product));
    setImage(null);
    setErrors({});
  };

  const setField = (key) => (event) => {
    const value = key === "is_active" ? event.target.checked : event.target.value;
    setErrors((previous) => ({ ...previous, [key]: undefined }));
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const handleSave = async () => {
    const isEdit = Boolean(editing?.id);
    const local = {};
    if (!form.name.trim()) local.name = "Nom requis.";
    if (!isEdit && !form.product_code.trim()) local.product_code = "Code requis.";
    if (form.unit_cost !== "" && Number(form.unit_cost) < 0) local.unit_cost = "Prix invalide.";
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    setSaving(true);
    try {
      const body = buildBody(form, image, isEdit);
      if (isEdit) await MplusApi.updateProduct(editing.id, body);
      else await MplusApi.createProduct(body);
      notify(isEdit ? "Modèle mis à jour." : "Modèle créé.", "success");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (error) {
      setErrors(fieldErrors(error));
      notify(error?.response?.status === 409 ? "Ce code produit existe déjà." : apiErrorMessage(error, "Enregistrement impossible."), "error");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <Box className={classes.fieldGroup} key={key}>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <TextField fullWidth size="small" value={form[key] ?? ""} error={Boolean(errors[key])} helperText={errors[key]} onChange={setField(key)} {...props} />
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" className={classes.entryDialog}>
      <DialogTitle className={classes.entryDialogTitle}>
        <Box>
          <Typography className={classes.dialogTitleText}>{editing ? (editing.id ? `Modifier · ${editing.name}` : "Nouveau modèle") : "Catalogue des modèles M+"}</Typography>
          <Typography className={classes.dialogSubtitle}>Prix, taux annuel, photo et caractéristiques des véhicules vendus en M+.</Typography>
        </Box>
        <IconButton aria-label="Fermer" onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent className={classes.entryDialogContent}>
        {editing ? (
          <Box className={classes.catalogForm}>
            {field("name", "Nom *")}
            {field("product_code", "Code produit *", { disabled: Boolean(editing.id), placeholder: "MPLUS-XP125" })}
            {field("category", "Catégorie", { placeholder: "Motorcycle - Fuel" })}
            {field("unit_cost", "Prix comptant (XAF)", { type: "number", inputProps: { min: 0 } })}
            {field("annual_interest_rate", "Taux annuel (%)", { type: "number", inputProps: { min: 0, step: 0.1 } })}
            {SELECTS.map((select) => (
              <Box className={classes.fieldGroup} key={select.key}>
                <Typography className={classes.fieldLabel}>{select.label}</Typography>
                <TextField select fullWidth size="small" value={form[select.key] || ""} error={Boolean(errors[select.key])} helperText={errors[select.key]} SelectProps={{ displayEmpty: true }} onChange={setField(select.key)}>
                  <MenuItem value="">Non renseigné</MenuItem>
                  {Object.entries(select.options).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
              </Box>
            ))}
            <Box className={`${classes.fieldGroup} ${classes.catalogWide}`}>
              <Typography className={classes.fieldLabel}>Caractéristiques</Typography>
              <TextField fullWidth multiline minRows={2} size="small" value={form.specs || ""} onChange={setField("specs")} placeholder="125 cm3, …" />
            </Box>
            <Box className={classes.fieldGroup}>
              <Typography className={classes.fieldLabel}>Photo</Typography>
              <Button component="label" className={classes.cancelButton}>
                {image ? image.name : "Choisir une image"}
                <input hidden type="file" accept="image/*" onChange={(event) => setImage(event.target.files?.[0] || null)} />
              </Button>
            </Box>
            <FormControlLabel control={<Checkbox size="small" checked={Boolean(form.is_active)} onChange={setField("is_active")} />} label="Modèle actif (visible des clients)" />
          </Box>
        ) : loading ? (
          <Box className={classes.dialogLoader}><CircularProgress size={28} /></Box>
        ) : (
          <Box className={classes.catalogList}>
            {products.length ? products.map((product) => {
              const imageUrl = toAssetUrl(product.image_url);
              return (
                <Box className={classes.catalogRow} key={product.id}>
                  {imageUrl ? <img src={imageUrl} alt="" /> : <DirectionsCarRoundedIcon />}
                  <Box className={classes.catalogInfo}>
                    <Typography className={classes.catalogName}>{product.name || "--"} {product.is_active === false && <span className={`${classes.statusBadge} ${classes.statusIndisponible}`}>Inactif</span>}</Typography>
                    <Typography className={classes.catalogMeta}>{[product.product_code, product.category, ...productFeatures(product)].filter(Boolean).join(" · ") || "--"}</Typography>
                  </Box>
                  <Box className={classes.catalogPrice}>
                    <b>{formatAmount(product.unit_cost)}</b>
                    <span>{product.annual_interest_rate != null ? `${product.annual_interest_rate} % / an` : "--"}</span>
                  </Box>
                  <IconButton aria-label={`Modifier ${product.name}`} onClick={() => startEdit(product)}><EditOutlinedIcon /></IconButton>
                </Box>
              );
            }) : <Typography className={classes.emptyText}>Aucun modèle dans le catalogue.</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.entryDialogActions}>
        {editing ? (
          <>
            <Button className={classes.cancelButton} onClick={() => setEditing(null)}>Retour à la liste</Button>
            <Button className={classes.saveButton} variant="contained" disabled={saving} onClick={handleSave}>{saving ? "Enregistrement..." : "Enregistrer"}</Button>
          </>
        ) : (
          <Button className={classes.saveButton} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => startEdit(null)}>Nouveau modèle</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductCatalogDialog;
